import { parseUnit } from "./units";

/**
 * Derive a dimension value from a Tailwind className string.
 * Supports important (!) and responsive prefixes (sm:, md:, lg:, xl:, 2xl:),
 * as well as arbitrary values like w-[200px] / h-[50%].
 */
export function deriveDimensionFromClassName(
  className: string | undefined,
  dimension: "width" | "height",
): number | "auto" | "full" | null {
  if (!className) {
    return null;
  }

  const prefix = dimension === "width" ? "w" : "h";

  const autoPattern = new RegExp(
    `(?:^|\\s)(?:!|(?:sm:|md:|lg:|xl:|2xl:):)?${prefix}-auto(?:\\s|$)`,
  );
  const fullPattern = new RegExp(
    `(?:^|\\s)(?:!|(?:sm:|md:|lg:|xl:|2xl:):)?${prefix}-full(?:\\s|$)`,
  );

  if (autoPattern.test(className)) {
    return "auto";
  }
  if (fullPattern.test(className)) {
    return "full";
  }

  // Arbitrary value patterns (e.g., w-[200px], !w-[200px], md:w-[200px], md:!w-[200px])
  const arbitraryPatterns = [
    new RegExp(`(?:^|\\s)${prefix}-\\[([^\\]]+)\\](?:\\s|$)`),
    new RegExp(`(?:^|\\s)!${prefix}-\\[([^\\]]+)\\](?:\\s|$)`),
    new RegExp(
      `(?:^|\\s)(?:sm:|md:|lg:|xl:|2xl:):${prefix}-\\[([^\\]]+)\\](?:\\s|$)`,
    ),
    new RegExp(
      `(?:^|\\s)(?:sm:|md:|lg:|xl:|2xl:):!${prefix}-\\[([^\\]]+)\\](?:\\s|$)`,
    ),
  ];

  for (const pattern of arbitraryPatterns) {
    const match = className.match(pattern);
    if (match) {
      const captured = match[1];
      if (!captured) {
        continue;
      }
      const parsed = parseUnit(
        captured,
        { value: 0, unit: "px" },
        "default",
        "px",
      );
      const numericValue =
        typeof parsed.value === "number"
          ? parsed.value
          : Number.parseFloat(parsed.value);

      // If value is invalid, skip
      if (Number.isNaN(numericValue)) {
        continue;
      }

      const unit = parsed.unit;
      if (unit === "px") {
        return numericValue;
      }
      if (unit === "%") {
        if (numericValue >= 98) {
          return "full";
        }
        return null;
      }

      // Other units not supported as fixed numeric values here
      return null;
    }
  }

  return null;
}
